import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CardContent,
  Grid,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import 'chart.js/auto';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../store/AuthContext';
import { Images } from '../assets';
import withComponentAuth from '../hoc/withComponentAuth';
import { RevenueChart } from './Dashboard/Components/Revenue';
import { WeeklyTaskCard } from './Dashboard/Components/Tasks';
import { DonutComponent } from './Dashboard/Components/Enquries';
import { PerformanceChart } from './Dashboard/Components/TotalPerf';
import { UserPerformanceCard } from './Dashboard/Components/TotalUserPerf';
import { DashboardBookings } from './Dashboard/Components/Bookings';
import { SearchTrigger } from '../components/global/SearchTrigger';
import { useGlobalSearch } from '../contexts/GlobalSearchContext';
import {
  formatDisplayName,
  getFirstName,
} from '../utils/displayName';
import {
  DashboardData,
  DashboardPeriod,
  DashboardTaskMetric,
  fetchDashboardStats,
  formatCompactCount,
  formatCurrency,
} from '../services/dashboard';
import { PageHeaderBar } from '../components/layout/PageHeaderBar';

const summaryIcons = [
  Images.GROUPICON,
  Images.TECHNICIANICON,
  Images.BOOKINGICON,
  Images.ENQUIRIESICON,
  Images.REVENUEICON,
];

const summaryLabels = [
  'Total Users',
  'Total Technicians',
  'Total Bookings',
  'Total Enquiry',
  'Total Revenue',
];

const Dashboard = () => {
  const { authState } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<DashboardPeriod>('weekly');
  const [taskMetric, setTaskMetric] = useState<DashboardTaskMetric>('bookings');
  const [localSearch, setLocalSearch] = useState('');
  const { registerLocalFilter } = useGlobalSearch();

  useEffect(() => {
    registerLocalFilter(setLocalSearch);
    return () => registerLocalFilter(null);
  }, [registerLocalFilter]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardStats({ period, taskMetric });
      setDashboardData(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load dashboard data',
      );
    } finally {
      setLoading(false);
    }
  }, [period, taskMetric]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const summaryCards = useMemo(() => {
    if (!dashboardData) return null;
    const { summary } = dashboardData;
    const values = [
      summary.totalUsers,
      summary.totalTechnicians,
      summary.totalBookings,
      summary.totalEnquiries,
      summary.totalRevenue,
    ];
    const lastWeekCounts = [
      summary.lastWeek.users,
      summary.lastWeek.technicians,
      summary.lastWeek.bookings,
      summary.lastWeek.enquiries,
      summary.lastWeek.revenue,
    ];

    return summaryLabels.map((label, index) => ({
      label,
      displayValue:
        index === 4
          ? formatCurrency(summary.totalRevenue)
          : formatCompactCount(values[index]),
      labelText: 'Last Week',
      count: lastWeekCounts[index] ?? 0,
    }));
  }, [dashboardData]);

  const displayName = formatDisplayName(authState.userName);

  return (
    <>
      <Header
        firstName={getFirstName(displayName)}
        loading={loading}
        onRefresh={loadDashboard}
      />
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <Box
        sx={{
          px: 2.5,
          py: 2,
          position: 'relative',
          bgcolor: 'background.default',
          minHeight: '100%',
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Typography
                component="span"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={loadDashboard}
              >
                Retry
              </Typography>
            }
          >
            {error}
          </Alert>
        )}

        {loading && !dashboardData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 200 }}>
            <CircularProgress color="inherit" />
          </Box>
        )}

        {dashboardData && (
          <Box sx={{ opacity: loading ? 0.65 : 1, transition: 'opacity 0.2s' }}>
            <Grid container spacing={2.5} mb={0.5}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {summaryCards?.map((item, index) => (
                    <Box flex={1} key={index} sx={{ minWidth: 170 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '16px',
                          backgroundColor: 'background.paper',
                          minHeight: 118,
                          px: 1.5,
                          py: 1.25,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: '0 0 72px', display: 'flex', justifyContent: 'center' }}>
                          <img
                            src={summaryIcons[index]}
                            alt={item.label}
                            style={{ width: 64, height: 64, objectFit: 'contain' }}
                          />
                        </Box>
                        <CardContent sx={{ flex: 1, py: '8px !important', px: '8px !important', '&:last-child': { pb: '8px !important' } }}>
                          <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}>
                            {item.displayValue}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#8A8D91', lineHeight: 1.3 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#666', mt: 0.5, lineHeight: 1.3 }}>
                            {item.labelText}{' '}
                            <Typography component="span" sx={{ color: '#0BE600', fontWeight: 700, fontSize: 12 }}>
                              +{formatCompactCount(item.count)}
                            </Typography>
                          </Typography>
                        </CardContent>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <DonutComponent enquiries={dashboardData.enquiries} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <WeeklyTaskCard
                    weeklyTasks={dashboardData.weeklyTasks}
                    loading={loading}
                    onPeriodChange={setPeriod}
                    onMetricChange={setTaskMetric}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2.5} alignItems="stretch" sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: 0 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <RevenueChart
                    revenue={dashboardData.revenue}
                    loading={loading}
                    onPeriodChange={setPeriod}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: 0 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <PerformanceChart
                    overallPerformance={dashboardData.overallPerformance}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: 0 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <UserPerformanceCard
                    userPerformance={dashboardData.userPerformance}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <DashboardBookings
                  bookings={dashboardData.recentBookings}
                  filterQuery={localSearch}
                  onClearSearch={() => setLocalSearch('')}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </>
  );
};

export default withComponentAuth(Dashboard);

const Header = ({
  firstName,
  loading,
  onRefresh,
}: {
  firstName: string;
  loading: boolean;
  onRefresh: () => void;
}) => (
  <PageHeaderBar>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Box>
        <Typography sx={{ fontSize: 11, color: '#8A8D91', lineHeight: 1.2 }}>
          Hi, {firstName}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
          Welcome Back
        </Typography>
      </Box>
      <Tooltip title="Refresh dashboard">
        <span>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={loading}
            sx={{ color: '#888', '&:hover': { color: '#fff' }, p: 0.5 }}
          >
            <Refresh sx={{ fontSize: 16, opacity: loading ? 0.5 : 1 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>

    <Box sx={{ flex: 1 }} />

    <Box sx={{ maxWidth: 280, width: '100%' }}>
      <SearchTrigger compact />
    </Box>
  </PageHeaderBar>
);
